/** Единые короткие подписи календарных осей, без точки. */
export const CHART_MONTHS_RU = [
  'Янв',
  'Фев',
  'Мар',
  'Апр',
  'Май',
  'Июн',
  'Июл',
  'Авг',
  'Сен',
  'Окт',
  'Ноя',
  'Дек',
] as const;

export type BarValue = number | null;
export interface BarAxis {
  min: number;
  max: number;
  ticks: number[];
  error: string | null;
}

const SAFE_INTEGER_LIMIT = Number.MAX_SAFE_INTEGER;
const INVALID_BAR_VALUE = 'Значение должно быть конечным числом в пределах безопасного диапазона.';

/**
 * Builds a zero-anchored numeric axis for signed columns. `null` means unavailable data;
 * numeric zero is a real value. Invalid numbers fail the whole model instead of disappearing.
 */
export function barAxis(values: readonly BarValue[]): BarAxis {
  let low = 0;
  let high = 0;
  let hasNonZero = false;

  for (const value of values) {
    if (value === null) continue;
    if (!Number.isFinite(value) || Math.abs(value) > SAFE_INTEGER_LIMIT) {
      return { min: 0, max: 1, ticks: [0, 1], error: INVALID_BAR_VALUE };
    }
    if (value !== 0) hasNonZero = true;
    low = Math.min(low, value);
    high = Math.max(high, value);
  }

  // An all-zero or all-missing dataset should still have a stable, useful axis.
  if (!hasNonZero) return { min: 0, max: 1, ticks: [0, 0.2, 0.4, 0.6, 0.8, 1], error: null };

  const axis = safeNiceTicks(low, high);
  return { ...axis, error: null };
}

function safeNiceTicks(min: number, max: number, count = 4): Pick<BarAxis, 'min' | 'max' | 'ticks'> {
  const span = max - min;
  const rawStep = span / count;
  const exponent = Math.floor(Math.log10(rawStep));
  const power = 10 ** exponent;
  if (!Number.isFinite(power) || power === 0) return { min, max, ticks: [...new Set([min, 0, max])] };
  const ratio = rawStep / power;
  const multiplier = ratio >= 7 ? 10 : ratio >= 3 ? 5 : ratio >= 1.5 ? 2 : 1;
  const step = multiplier * power;
  let firstIndex = Math.floor(min / step);
  let lastIndex = Math.ceil(max / step);

  // Rounding at a step boundary can put a mathematically integral ratio a few ulps away.
  const nearInteger = (n: number) =>
    Math.abs(n - Math.round(n)) <= Number.EPSILON * Math.max(2, Math.abs(n)) * 4;
  const minRatio = min / step;
  const maxRatio = max / step;
  if (nearInteger(minRatio)) firstIndex = Math.round(minRatio);
  if (nearInteger(maxRatio)) lastIndex = Math.round(maxRatio);

  const clean = (value: number) => (Object.is(value, -0) ? 0 : Number(value.toPrecision(15)));
  const ticks: number[] = [];
  for (let index = firstIndex; index <= lastIndex && ticks.length < 16; index++) {
    ticks.push(clean(index * step));
  }

  const axisMin = clean(firstIndex * step);
  const axisMax = clean(lastIndex * step);
  // Keep the data domain inclusive even if decimal cleanup rounds a tick inward.
  return { min: Math.min(axisMin, min), max: Math.max(axisMax, max), ticks };
}

export interface HeatmapCell {
  date: string;
  value: number | null;
  level: 0 | 1 | 2 | 3 | 4 | null;
}

export interface DailyHeatmap {
  /** Weeks are columns; each column contains Monday through Sunday. Null cells are outside the range. */
  weeks: (HeatmapCell | null)[][];
  /** Fixed scale maximum used to calculate levels. */
  max: number;
  error: string | null;
}

interface ParsedDate {
  year: number;
  month: number;
  day: number;
  ordinal: number;
}

const INVALID_DATE = 'Дата должна существовать и иметь формат YYYY-MM-DD.';
const INVALID_RANGE = 'Диапазон должен идти по возрастанию и занимать не больше 366 дней.';
const INVALID_HEAT_VALUE = 'Нагрузка должна быть конечным числом от нуля до безопасного предела.';
const INVALID_DOMAIN = 'Максимум шкалы должен быть конечным числом больше нуля.';

/**
 * Builds a Monday-first calendar grid using Gregorian day ordinals, independent of local time zones.
 * A missing key and explicit null both render as an empty in-range day; zero has level 0.
 */
export function buildDailyHeatmap(
  fromISO: string,
  toISO: string,
  values: Readonly<Record<string, number | null>>,
  domainMax?: number,
): DailyHeatmap {
  const fallbackMax = domainMax !== undefined && Number.isFinite(domainMax) && domainMax > 0 ? domainMax : 1;
  const fail = (error: string): DailyHeatmap => ({ weeks: [], max: fallbackMax, error });
  const from = parseISODate(fromISO);
  const to = parseISODate(toISO);
  if (!from || !to) return fail(INVALID_DATE);
  if (to.ordinal < from.ordinal || to.ordinal - from.ordinal + 1 > 366) return fail(INVALID_RANGE);
  if (domainMax !== undefined && (!Number.isFinite(domainMax) || domainMax <= 0)) return fail(INVALID_DOMAIN);

  const inRangeValues = new Map<string, number | null>();
  let observedMax = 0;
  for (const [date, value] of Object.entries(values)) {
    const parsed = parseISODate(date);
    if (!parsed) return fail(INVALID_DATE);
    if (parsed.ordinal < from.ordinal || parsed.ordinal > to.ordinal) continue;
    if (value !== null && (!Number.isFinite(value) || value < 0 || value > SAFE_INTEGER_LIMIT)) {
      return fail(INVALID_HEAT_VALUE);
    }
    inRangeValues.set(date, value);
    if (typeof value === 'number' && value > observedMax) observedMax = value;
  }

  const max = domainMax ?? (observedMax > 0 ? observedMax : 1);
  const startOrdinal = from.ordinal - weekdayMondayFirst(from.ordinal);
  const endOrdinal = to.ordinal + (6 - weekdayMondayFirst(to.ordinal));
  const weeks: (HeatmapCell | null)[][] = [];

  for (let monday = startOrdinal; monday <= endOrdinal; monday += 7) {
    const week: (HeatmapCell | null)[] = [];
    for (let weekday = 0; weekday < 7; weekday++) {
      const ordinal = monday + weekday;
      if (ordinal < from.ordinal || ordinal > to.ordinal) {
        week.push(null);
        continue;
      }
      const date = formatISODate(ordinal);
      const value = inRangeValues.has(date) ? (inRangeValues.get(date) ?? null) : null;
      week.push({ date, value, level: intensity(value, max) });
    }
    weeks.push(week);
  }

  return { weeks, max, error: null };
}

function intensity(value: number | null, max: number): HeatmapCell['level'] {
  if (value === null) return null;
  if (value === 0) return 0;
  if (value >= max) return 4;
  return Math.max(1, Math.ceil((value / max) * 4)) as 1 | 2 | 3 | 4;
}

function parseISODate(value: string): ParsedDate | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/u.test(value)) return null;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (year < 1 || year > 9999 || month < 1 || month > 12) return null;
  const monthLengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (day < 1 || day > monthLengths[month - 1]) return null;
  return { year, month, day, ordinal: dayOrdinal(year, month, day) };
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function dayOrdinal(year: number, month: number, day: number): number {
  const priorYears = year - 1;
  let ordinal =
    priorYears * 365 +
    Math.floor(priorYears / 4) -
    Math.floor(priorYears / 100) +
    Math.floor(priorYears / 400);
  const monthLengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  for (let index = 0; index < month - 1; index++) ordinal += monthLengths[index];
  return ordinal + day - 1;
}

function weekdayMondayFirst(ordinal: number): number {
  // Gregorian 0001-01-01 is Monday and ordinal 0.
  return ordinal % 7;
}

function formatISODate(ordinal: number): string {
  let low = 1;
  let high = 9999;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (yearStart(middle) <= ordinal) low = middle;
    else high = middle - 1;
  }
  const year = low;
  let dayOfYear = ordinal - yearStart(year);
  const monthLengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let month = 0;
  while (dayOfYear >= monthLengths[month]) dayOfYear -= monthLengths[month++];
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(year).padStart(4, '0')}-${pad(month + 1)}-${pad(dayOfYear + 1)}`;
}

function yearStart(year: number): number {
  const priorYears = year - 1;
  return (
    priorYears * 365 +
    Math.floor(priorYears / 4) -
    Math.floor(priorYears / 100) +
    Math.floor(priorYears / 400)
  );
}
